import { TaskItem } from '@demind-inc/core';

import { AddTodoTaskItemProps } from '../../data-access';
import {
  getFormattedDueDateForAddTask,
  getFormattedDueDateForUpdateTask,
  getIsOverdue,
  getStartDateTimeFromDuration,
} from '../../helpers';

type TaskDetailLike = Pick<
  TaskItem,
  | 'name'
  | 'desc'
  | 'aiNote'
  | 'dueDateTime'
  | 'duration'
  | 'labels'
  | 'sortIndex'
  | 'addedToCalendar'
  | 'priority'
  | 'recurring'
>;

type RecurringPayload = NonNullable<TaskItem['recurring']>;

type BuildCreateTaskPayloadParams = {
  name: string;
  desc: string;
  aiNote: string;
  taskInfo: TaskDetailLike;
  appFrom: TaskItem['appFrom'];
  isClickUpTask: boolean;
  recurringFallback: RecurringPayload;
};

type BuildUpdateTaskPayloadParams = {
  name: string;
  desc: string;
  aiNote: string;
  taskInfo: TaskDetailLike;
  defaultTask: TaskItem;
  isClickUpTask: boolean;
  recurringFallback: RecurringPayload;
  shouldAddToCalendar: boolean;
  findPhaseForTaskTime: (
    startDatetime: string,
    endDatetime: string
  ) => TaskItem['addedZonePhase'] | null | undefined;
};

export const buildCreateTaskPayload = ({
  name,
  desc,
  aiNote,
  taskInfo,
  appFrom,
  isClickUpTask,
  recurringFallback,
}: BuildCreateTaskPayloadParams): {
  payload: AddTodoTaskItemProps;
  shouldReflectToEvent: boolean;
} => {
  const isEligibleToAddToCalendar =
    !!taskInfo.dueDateTime?.datetime && !!taskInfo.duration?.amount;

  return {
    shouldReflectToEvent: isEligibleToAddToCalendar,
    payload: {
      name: name || taskInfo.name,
      desc: desc || taskInfo.desc,
      aiNote,
      ...getFormattedDueDateForAddTask('due', taskInfo.dueDateTime),
      ...getStartDateTimeFromDuration(taskInfo.duration, taskInfo.dueDateTime),
      appFrom: appFrom || 'lifestack',
      labels: taskInfo.labels ?? [],
      assigneesIds: [],
      duration: taskInfo.duration,
      addedToCalendar: isEligibleToAddToCalendar,
      completed: false,
      rawJson: {},
      sortIndex: taskInfo.sortIndex,
      priority: taskInfo.priority,
      updatedAt: new Date().toISOString(),
      ...(isClickUpTask
        ? {}
        : {
            recurring: taskInfo.recurring || recurringFallback,
          }),
    },
  };
};

export const buildUpdateTaskPayload = ({
  name,
  desc,
  aiNote,
  taskInfo,
  defaultTask,
  isClickUpTask,
  recurringFallback,
  shouldAddToCalendar,
  findPhaseForTaskTime,
}: BuildUpdateTaskPayloadParams) => {
  const startDateTime = getStartDateTimeFromDuration(taskInfo.duration, taskInfo.dueDateTime);

  return {
    startDateTime,
    newTaskInfo: {
      name: name || taskInfo.name,
      desc: desc || taskInfo.desc,
      aiNote,
      ...getFormattedDueDateForUpdateTask('due', taskInfo.dueDateTime),
      ...startDateTime,
      ...(startDateTime.startDateTime?.datetime &&
        taskInfo.dueDateTime.datetime &&
        taskInfo.addedToCalendar && {
          addedZonePhase: findPhaseForTaskTime(
            startDateTime.startDateTime.datetime,
            taskInfo.dueDateTime.datetime
          ),
        }),
      duration: taskInfo.duration,
      labels: taskInfo?.labels ?? [],
      appFrom: defaultTask.appFrom,
      originalTaskId: defaultTask.originalTaskId,
      priority: taskInfo.priority,
      ...(isClickUpTask
        ? {}
        : {
            recurring: taskInfo.recurring || recurringFallback,
          }),
      addedToCalendar: shouldAddToCalendar ? true : taskInfo.addedToCalendar,
    },
  };
};

export const buildMovedTaskParam = (prevDate: string | undefined, newDate: string | undefined) => {
  if (!prevDate || !newDate || prevDate === newDate) {
    return undefined;
  }

  return {
    prevDate,
    newDate,
    wasOverdue: getIsOverdue(prevDate),
  };
};
